import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, Building, Calendar, ChevronDown, X } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getExportFileName } from '../../utils/fileExportNaming';
import { getClientFormatById } from '../../api/createClientApi';
import './ClientFormatDetails.css';

/* ─── Helper: Generate column config from data keys ── */
const generateColConfig = (data) => {
  if (!data || data.length === 0) return [];
  const keys = Object.keys(data[0]);

  // Determine width class based on label length
  const getWidthClass = (label) => {
    const len = label.length;
    if (len <= 8) return 'cfd-cell-narrow';
    if (len <= 12) return 'cfd-cell-std';
    if (len <= 18) return 'cfd-cell-mid';
    if (len <= 24) return 'cfd-cell-wide';
    return 'cfd-cell-xwide';
  };

  // All fields center-aligned
  const getAlignClass = () => {
    return 'cfd-align-center';
  };

  return [
    { key: 'chk', label: '', cls: 'cfd-cell-chk', align: 'center' },
    ...keys.map(key => {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
      return {
        key,
        label,
        cls: getWidthClass(label),
        alignClass: getAlignClass(key, label)
      };
    })
  ];
};

/* ─── Helper: Format cell value ── */
const formatCellValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }
  return value;
};

/* ─── Table Row — pure component to handle dynamic columns ── */
const TableRow = React.memo(({ row, isSelected, onSelect, rowIndex, columns }) => (
  <div className="cfd-table-row">
    {columns.map((col) =>
      col.key === 'chk'
        ? <div key={col.key} className="cfd-cell cfd-cell-chk">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => onSelect(rowIndex, e.target.checked)}
            />
          </div>
        : <div key={col.key} className={`cfd-cell cfd-cell-data ${col.cls} ${col.alignClass || 'cfd-align-left'}`}>{formatCellValue(row[col.key])}</div>
    )}
  </div>
));

/* ─── Main component ─────────────────────────────────────── */
const ClientFormatDetails = ({ onBack, date, formatId }) => {
  const [hasError, setHasError] = useState(false);
  const [showFormatMenu, setShowFormatMenu] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [clientName, setClientName] = useState('');
  const [filters] = useState({
    startDate: '',
    endDate: '',
    clientName: '',
    vehicleType: '',
    sortBy: 'date',
    sortOrder: 'desc'
  });

  const fetchFormatDetails = useCallback(async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const response = await getClientFormatById(formatId);
      const formatData = response.data || response;

      // Set client name from response
      setClientName(formatData.client?.clientName || formatData.clientName || 'Unknown Client');

      // Get sheet data - preserve original field names as they come from API
      const entries = formatData.sheetData || formatData.tripSheetEntries || formatData.entries || formatData.data || [];

      // Generate columns dynamically from the first entry's keys
      if (entries.length > 0) {
        setColumns(generateColConfig(entries));
      } else {
        setColumns([]);
      }

      setRows(entries);
    } catch (err) {
      setFetchError('Failed to load trip sheet details');
    } finally {
      setIsLoading(false);
    }
  }, [formatId]);

  /* Fetch data from API */
  useEffect(() => {
    if (formatId) {
      fetchFormatDetails();
    }
  }, [formatId, fetchFormatDetails]);

  /* Auto-dismiss error toast after 3s */
  useEffect(() => {
    if (!hasError) return;
    const t = setTimeout(() => setHasError(false), 3000);
    return () => clearTimeout(t);
  }, [hasError]);

  const filteredRows = rows.filter(row => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = Object.values(row).some(value =>
        String(value).toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Apply client filter
    if (filters.clientName) {
      const rowClient = row['Vendor'] || row.vendor || row['Subvendor Name'] || row.subvendor || '';
      if (rowClient !== filters.clientName) return false;
    }

    // Apply date range filter
    if (filters.startDate || filters.endDate) {
      const rowDate = row['Leg Date'] || row.legDate || '';
      if (rowDate) {
        const dateValue = new Date(rowDate);
        if (filters.startDate && dateValue < new Date(filters.startDate)) return false;
        if (filters.endDate && dateValue > new Date(filters.endDate)) return false;
      }
    }

    return true;
  }).sort((a, b) => {
    // Apply sorting
    const { sortBy, sortOrder } = filters;
    let valA, valB;

    switch (sortBy) {
      case 'date':
        valA = new Date(a['Leg Date'] || a.legDate || 0);
        valB = new Date(b['Leg Date'] || b.legDate || 0);
        break;
      case 'client':
        valA = (a['Vendor'] || a.vendor || '').toLowerCase();
        valB = (b['Vendor'] || b.vendor || '').toLowerCase();
        break;
      case 'vehicle':
        valA = (a['Vehicle Id'] || a.vehicleId || '').toLowerCase();
        valB = (b['Vehicle Id'] || b.vehicleId || '').toLowerCase();
        break;
      case 'tripId':
        valA = a['Trip Id'] || a.tripId || '';
        valB = b['Trip Id'] || b.tripId || '';
        break;
      default:
        return 0;
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allRowIds = rows.map((_, index) => index);
      setSelectedRows(new Set(allRowIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const handleRowSelect = (rowIndex, checked) => {
    const newSelectedRows = new Set(selectedRows);
    if (checked) {
      newSelectedRows.add(rowIndex);
    } else {
      newSelectedRows.delete(rowIndex);
    }
    setSelectedRows(newSelectedRows);
    setSelectAll(newSelectedRows.size === rows.length);
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text(`Trip Sheet Report - ${clientName || 'Client'}`, 20, 20);
    
    // Add date
    doc.setFontSize(12);
    doc.text(`Date: ${date || new Date().toLocaleDateString()}`, 20, 30);
    
    // Determine which data to use
    const hasSelection = selectedRows.size > 0;
    const dataToUse = hasSelection ? Array.from(selectedRows).map(index => rows[index]) : rows;
    
    // Add selection info
    doc.setFontSize(10);
    if (hasSelection) {
      doc.text(`Selected Records: ${selectedRows.size} of ${rows.length}`, 20, 40);
    } else {
      doc.text(`Total Records: ${rows.length}`, 20, 40);
    }
    
    // Prepare table data - use dynamic columns (skip checkbox column)
    const dataColumns = columns.filter(col => col.key !== 'chk');
    const headers = dataColumns.map(col => col.label);
    const tableData = dataToUse.map(row =>
      dataColumns.map(col => String(row[col.key] ?? '-'))
    );

    // Add table
    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 50,
      headStyles: {
        fillColor: [32, 185, 103],
        textColor: 255,
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      styles: {
        fontSize: 7,
        cellPadding: 1,
        overflow: 'linebreak'
      },
      margin: { left: 10, right: 10 }
    });
    
    // Save the PDF
    const clientNameSafe = (clientName || 'Client').replace(/\s+/g, '_');
    const pdfReport = hasSelection 
      ? `${clientNameSafe}_Selected_${selectedRows.size}_Records`
      : `${clientNameSafe}_TripSheet`;
    const fileName = getExportFileName({
      reportName: pdfReport,
      data: dataToUse,
      fromDate: filters.startDate,
      toDate: filters.endDate,
      fallbackDate: date,
      ext: 'pdf',
    });
    doc.save(fileName);
    setShowFormatMenu(false);
  };

  const downloadExcel = () => {
    // Determine which data to use
    const hasSelection = selectedRows.size > 0;
    const dataToUse = hasSelection ? Array.from(selectedRows).map(index => rows[index]) : rows;
    
    // Use dynamic columns for export (skip checkbox column)
    const dataColumns = columns.filter(col => col.key !== 'chk');
    const headers = dataColumns.map(col => col.label);
    const exportData = dataToUse.map(row =>
      dataColumns.map(col => row[col.key] ?? '-')
    );

    const tableData = [headers, ...exportData];

    const ws = XLSX.utils.aoa_to_sheet(tableData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Trip Sheet');
    
    const clientNameSafe = (clientName || 'Client').replace(/\s+/g, '_');
    const excelReport = hasSelection 
      ? `${clientNameSafe}_Selected_${selectedRows.size}_Records`
      : `${clientNameSafe}_TripSheet`;
    const fileName = getExportFileName({
      reportName: excelReport,
      data: dataToUse,
      fromDate: filters.startDate,
      toDate: filters.endDate,
      fallbackDate: date,
      ext: 'xlsx',
    });
    XLSX.writeFile(wb, fileName);
    setShowFormatMenu(false);
  };

  const downloadCSV = () => {
    // Determine which data to use
    const hasSelection = selectedRows.size > 0;
    const dataToUse = hasSelection ? Array.from(selectedRows).map(index => rows[index]) : rows;
    
    // Use dynamic columns for export (skip checkbox column)
    const dataColumns = columns.filter(col => col.key !== 'chk');
    const headers = dataColumns.map(col => col.label);
    const exportData = dataToUse.map(row =>
      dataColumns.map(col => row[col.key] ?? '-')
    );

    const tableData = [headers, ...exportData];

    const csvContent = tableData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const clientNameSafe = (clientName || 'Client').replace(/\s+/g, '_');
    const csvReport = hasSelection 
      ? `${clientNameSafe}_Selected_${selectedRows.size}_Records`
      : `${clientNameSafe}_TripSheet`;
    const fileName = getExportFileName({
      reportName: csvReport,
      data: dataToUse,
      fromDate: filters.startDate,
      toDate: filters.endDate,
      fallbackDate: date,
      ext: 'csv',
    });
    
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowFormatMenu(false);
  };

  return (
    <div className="cfd-container">

      {/* ── Error toast ── */}
      {hasError && (
        <div className="cfd-error-toast">
          Error: Please fill all filter fields
          <X size={18} style={{ cursor: 'pointer' }} onClick={() => setHasError(false)} />
        </div>
      )}
      {fetchError && (
        <div className="cfd-error-toast">
          {fetchError}
          <X size={18} style={{ cursor: 'pointer' }} onClick={() => setFetchError('')} />
        </div>
      )}

      {/* ── TOP BAR ── */}
      <TopBar onBack={onBack} date={date} clientName={clientName} onDownload={() => setShowFormatMenu(!showFormatMenu)} showFormatMenu={showFormatMenu} downloadPDF={downloadPDF} downloadExcel={downloadExcel} downloadCSV={downloadCSV} onCloseFormatMenu={() => setShowFormatMenu(false)} searchTerm={searchTerm} onSearchChange={setSearchTerm} filteredCount={filteredRows.length} />
      
      {searchTerm && (
        <div style={{ padding: '8px 16px', margin: '0 20px', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '6px', fontSize: '14px', color: '#0369a1', marginBottom: '12px' }}>
          Found {filteredRows.length} record{filteredRows.length !== 1 ? 's' : ''} matching "{searchTerm}"
        </div>
      )}

      {/* ── TABLE ── */}
      <div className="cfd-table-wrapper">
        <div className="cfd-table-scroll custom-scrollbar hide-vertical-scrollbar">

          {/* TABLE HEADER */}
          <div className="cfd-table-header">
            {columns.map((col) =>
              col.key === 'chk'
                ? <div key={col.key} className="cfd-cell cfd-cell-chk">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      disabled={isLoading || rows.length === 0}
                    />
                  </div>
                : <div key={col.key} className={`cfd-cell cfd-cell-head ${col.cls} ${col.alignClass || 'cfd-align-left'}`}>
                    <div className="cfd-singleline-header">{col.label}</div>
                  </div>
            )}
          </div>

          {/* TABLE BODY */}
          <div className="cfd-table-body">
            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                Loading trip sheet details...
              </div>
            ) : filteredRows.length > 0 ? (
              filteredRows.map((row, i) => (
                <TableRow
                  key={i}
                  row={row}
                  isSelected={selectedRows.has(i)}
                  onSelect={handleRowSelect}
                  rowIndex={i}
                  columns={columns}
                />
              ))
            ) : (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666', fontSize: '14px' }}>
                {searchTerm ? `No records found matching "${searchTerm}"` : 'No trip sheet data available'}
              </div>
            )}
          </div>

        </div>
      </div>

    </div>
  );
};

/* ─── Top Bar ─────────────────────────────────────────────── */
const TopBar = ({ onBack, date, clientName, onDownload, showFormatMenu, downloadPDF, downloadExcel, downloadCSV, onCloseFormatMenu, searchTerm, onSearchChange, filteredCount }) => (
  <div className="cfd-topbar">
    <div className="cfd-topbar-left">
      <button className="cfd-back-btn" onClick={onBack}>
        <ChevronDown size={20} color="#20B967" style={{ transform: 'rotate(90deg)' }} />
      </button>
      <div className="cfd-topbar-info">
        <Building size={20} color="#20B967" />
        <span className="cfd-topbar-title">{clientName || 'Client'}</span>
      </div>
      <div className="cfd-topbar-info">
        <Calendar size={20} color="#20B967" />
        <span className="cfd-topbar-date">{date || new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
      </div>
    </div>
    <div className="cfd-topbar-right">
      <div className="cfd-search-bar">
        <input 
          type="text" 
          placeholder="Cab ID or Registration" 
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        <Search size={16} color="#555" />
      </div>
      <div style={{ position: 'relative' }}>
        <button className="cfd-btn-download" onClick={onDownload}>
          Download <Download size={15} />
        </button>
        {showFormatMenu && (
          <div style={{ 
            position: 'absolute', 
            top: '40px', 
            right: '0', 
            backgroundColor: 'white', 
            border: '1px solid #e0e0e0', 
            borderRadius: '8px', 
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)', 
            zIndex: 1000,
            minWidth: '120px'
          }}>
            <button
              onClick={downloadPDF}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: 'none', 
                backgroundColor: 'transparent', 
                textAlign: 'left', 
                cursor: 'pointer', 
                fontSize: '13px',
                borderRadius: '8px 8px 0 0'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              PDF
            </button>
            <button
              onClick={downloadExcel}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: 'none', 
                backgroundColor: 'transparent', 
                textAlign: 'left', 
                cursor: 'pointer', 
                fontSize: '13px',
                borderTop: '1px solid #e0e0e0'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              Excel
            </button>
            <button
              onClick={downloadCSV}
              style={{ 
                width: '100%', 
                padding: '8px 12px', 
                border: 'none', 
                backgroundColor: 'transparent', 
                textAlign: 'left', 
                cursor: 'pointer', 
                fontSize: '13px',
                borderTop: '1px solid #e0e0e0',
                borderRadius: '0 0 8px 8px'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
            >
              CSV
            </button>
          </div>
        )}
      </div>
    </div>
  </div>
);

export default ClientFormatDetails;
